import { useEffect, useState } from "react";
import { FloatingLabel, Form, Spinner } from "react-bootstrap";
import { UserResponseModel } from "model/users-model";
import { useGetCountriesQuery } from "api/countryApi";
import { Countries } from "model/countries-model";
import { RoleModel } from "model/roles-permission-model";
import { userSchema } from "validation-schema/user-schema";
import { useFormik } from "formik";
import {
  showToastError,
  showToastSuccess,
} from "components/toastify/ToastNotifications";
import {
  useCreateUsersMutation,
  useGetPermissionsByIdQuery,
  useResetUsersPasswordMutation,
  useUpdateUsersMutation,
} from "api/userManagementApi";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { IoMdCopy } from "react-icons/io";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Select from "react-select";

type Props = {
  activeModalTab: string;
  modalUserData: UserResponseModel | undefined;
  handleHideModal: Function;
  refetchUser: () => void;
  rolesList: RoleModel[] | undefined;
};

interface SingleValue {
  label: string;
  value: string;
}

interface Options {
  label: string;
  value: string;
}

const UserModalTabs = ({
  activeModalTab,
  modalUserData,
  handleHideModal,
  refetchUser,
  rolesList,
}: Props) => {
  const [activeTab, setActiveTab] = useState(activeModalTab);
  const [suspendConfirm, setSuspendConfirm] = useState(false);
  const [phoneValidation, setPhoneValidation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subActiveTab, setSubActiveTab] = useState("user");
  const [pass, setPass] = useState("password");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState<any>(modalUserData?.phone);
  const [selectedCountry, setSelectedCountry] = useState<SingleValue | null>({
    label: "",
    value: "",
  });

  const {
    data: countriesData,
    error: countriesError,
    isFetching: countriesIsFetching,
  } = useGetCountriesQuery();
  const [createUsers, createUsersResult] = useCreateUsersMutation();
  const [updateUsers, updateUsersResult] = useUpdateUsersMutation();
  const [resetUsersPassword, resetUserPasswordResult] =
    useResetUsersPasswordMutation();

  const [countries, setCountries] = useState<Countries[]>([]);
  const [options, setOptions] = useState<Options[]>();

  useEffect(() => {
    if (countriesIsFetching) {
      //setLoading(true);
    } else {
      //setLoading(false);
      setCountries(countriesData?.data as Countries[]);
      setOptions(
        countriesData?.data?.map((item) => ({
          label: item?.country,
          value: item?.id,
        }))
      );

      const currentCountry = countriesData?.data?.filter(
        (country) => country.country === modalUserData?.country
      )[0];
      if (currentCountry) {
        setSelectedCountry({
          label: currentCountry?.country,
          value: currentCountry.id,
        });
      }
    }
  }, [countriesIsFetching, countriesError]);

  

  const formik = useFormik({
    initialValues: {
      firstName: modalUserData?.firstName || "",
      lastName: modalUserData?.lastName || "",
      phoneNumber: modalUserData?.phone || phoneNumber,
      roleName: modalUserData?.role || "",
      email: modalUserData?.email || "",
      countryId: findCountryIdByName(modalUserData?.country) || "",
    },
    validationSchema: userSchema,
    onSubmit: async (values) => {
      if (phoneNumber && isValidPhoneNumber(phoneNumber)) {
        if (activeModalTab === "add-user") {
          try {
            setLoading(true);
            const model = {
              ...values,
              phoneNumber: phoneNumber,
              countryId: selectedCountry?.value,
            };
            const response = await createUsers(model).unwrap();
            if (response) {
              showToastSuccess(response?.ResponseMessage);
              handleHideModal();
              refetchUser();
            }
          } catch (error: any) {
            showToastError(error?.data.ResponseMessage);
          } finally {
            setLoading(false);
          }
        } else {
          try {
            setLoading(true);
            const currentCountry = options?.filter(
              (item) => item?.label === modalUserData?.country
            );
            const model = {
              ...values,
              userId: modalUserData?.userId,
              phoneNumber: phoneNumber ? phoneNumber : modalUserData?.phone,
              countryId:
                selectedCountry?.value ||
                (currentCountry && currentCountry[0].value),
            };
            const response = await updateUsers(model).unwrap();
            if (response) {
              showToastSuccess(response?.ResponseMessage);
              handleHideModal();
              refetchUser();
            }
          } catch (error: any) {
            showToastError(error?.data.ResponseMessage);
          } finally {
            setLoading(false);
          }
        }
      } else {
        setPhoneValidation(true);
      }
    },
  });

  const handleResetPass = async (e: any) => {
    e.preventDefault();
    try {
      setLoading(true);
      const model = { password: password, userId: modalUserData?.userId };
      const response = await resetUsersPassword(model).unwrap();
      if (response) {
        showToastSuccess(response?.ResponseMessage);
        handleHideModal();
      }
    } catch (error: any) {
      showToastError(error?.data.ResponseMessage);
    } finally {
      setLoading(false);
    }
  };

  const selectedRole =
    rolesList && rolesList.find((role) => role.name === formik.values.roleName);
  const {
    data: permissionByIdData,
    error: permissionByIdError,
    isFetching: permissionByIdIsFetching,
    refetch: refetchPermissionById,
  } = useGetPermissionsByIdQuery(selectedRole?.id || "");

  useEffect(() => {
    if (formik.values.roleName) {
      refetchPermissionById();
    }
  }, [formik.values.roleName]);

  function findCountryIdByName(countryName: any) {
    const selectedCountry = countries?.find(
      (country) => country.country === countryName
    );
    return selectedCountry ? selectedCountry.id : null;
  }

  const handleCopyPassword = async () => {
    await navigator.clipboard.writeText(password);
    showToastSuccess("Copied to clipboard");
  };

  const generateRandomPassword = () => {
    const uppercaseLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowercaseLetters = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const symbols = "!@#$%^&*()_-+=<>?";

    const getRandomChar = (charSet: string) => {
      const randomIndex = Math.floor(Math.random() * charSet.length);
      return charSet.charAt(randomIndex);
    };

    // Ensure at least one character from each set
    const requiredCharacters = [
      getRandomChar(uppercaseLetters),
      getRandomChar(lowercaseLetters),
      getRandomChar(numbers),
      getRandomChar(symbols),
    ];

    // Fill the remaining characters with a mix of all sets
    const allCharacters =
      uppercaseLetters + lowercaseLetters + numbers + symbols;
    for (let i = requiredCharacters.length; i < 8; i++) {
      requiredCharacters.push(getRandomChar(allCharacters));
    }

    // Shuffle the characters to randomize the order
    const shuffledPassword = requiredCharacters
      .sort(() => Math.random() - 0.5)
      .join("");
    setPassword(shuffledPassword);
  };

  return (
    <div>
      {activeTab === "add-user" && (
        <div>
          <h5 className="fw-bold mb-4 mx-1">Add a user</h5>
          <p>Fill the details below to invite a user</p>
          <Form className="mx-1" onSubmit={formik.handleSubmit}>
            <FloatingLabel
              controlId="floatingInput"
              label="Firstname"
              className="float-input my-4"
            >
              <Form.Control
                type="text"
                name="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Firstname"
              />
              {formik.touched.firstName && formik.errors.firstName && (
                <span className="text-danger font-size-sm">
                  {formik.errors.firstName}
                </span>
              )}
            </FloatingLabel>
            <FloatingLabel
              controlId="floatingInput"
              label="Lastname"
              className="float-input my-4"
            >
              <Form.Control
                type="text"
                name="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Lastname"
              />
              {formik.touched.lastName && formik.errors.lastName && (
                <span className="text-danger font-size-sm">
                  {formik.errors.lastName}
                </span>
              )}
            </FloatingLabel>
            <div className="my-4">
              <PhoneInput
                placeholder="Enter phone number"
                international
                value={phoneNumber}
                defaultCountry="US"
                onChange={(phone) => setPhoneNumber(phone)}
                inputProps={{ name: "phoneNumber", required: true }}
                countryCallingCodeEditable={false}
                className="d-flex justify-content-between w-100"
              />
              <span className="text-danger font-size-sm">
                {phoneValidation
                  ? phoneNumber
                    ? isValidPhoneNumber(phoneNumber)
                      ? undefined
                      : "Invalid phone number"
                    : "Phone number required"
                  : null}
              </span>
            </div>
            <FloatingLabel
              controlId="floatingInput"
              label="Email address"
              className="float-input my-4"
            >
              <Form.Control
                type="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder=""
              />
              {formik.touched.email && formik.errors.email && (
                <span className="text-danger font-size-sm">
                  {formik.errors.email}
                </span>
              )}
            </FloatingLabel>
            <FloatingLabel
              controlId="floatingSelect"
              label="Select Role"
              className="float-input"
            >
              <Form.Select
                aria-label="Floating label select example"
                name="roleName"
                value={formik.values.roleName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="my-4"
              >
                <option value="">Select Role</option>
                {rolesList?.map((role, key) => (
                  <option key={key} value={role?.name}>
                    {role?.name}
                  </option>
                ))}
              </Form.Select>
              {formik.touched.roleName && formik.errors.roleName && (
                <span className="text-danger font-size-sm">
                  {formik.errors.roleName}
                </span>
              )}
            </FloatingLabel>
            <div className="bg-lighter-blue mt-2 p-2 rounded-2">
              <p className="fw-bolder mb-1">Role's Permission</p>
              {permissionByIdData?.data?.permissions?.map((role) => (
                <p className="py-1 ps-2 m-0">&#x2022; {role}</p>
              ))}
            </div>
            <Select
              onChange={(option) => setSelectedCountry(option)}
              placeholder="Country"
              options={options}
              className="my-4"
            />
            <button
              disabled={loading}
              type="submit"
              className="btn-primary w-100 rounded-5 my-3"
            >
              Add user {loading && <Spinner size="sm" />}
            </button>
          </Form>
        </div>
      )}

      {activeTab === "view-user" && (
        <div>
          <h5 className="fw-bold my-4 mx-1">{`${modalUserData?.firstName} ${modalUserData?.lastName}`}</h5>
          <p className="mx-1">{modalUserData?.role}</p>
          <div className="mx-1">
            <button
              onClick={() => setSubActiveTab("user")}
              className={
                subActiveTab === "user"
                  ? "btn-primary-outline font-size-sm first-font-medium px-3 me-3"
                  : "btn-secondary-outline font-size-sm first-font-medium px-3 me-3"
              }
            >
              General
            </button>
            <button
              onClick={() => setSubActiveTab("password")}
              className={
                subActiveTab === "password"
                  ? "btn-primary-outline font-size-sm first-font-medium px-3 me-3"
                  : "btn-secondary-outline font-size-sm first-font-medium px-3 me-3"
              }
            >
              Reset Password
            </button>
          </div>
          {subActiveTab === "user" && (
            <Form className="mx-1" onSubmit={formik.handleSubmit}>
              <FloatingLabel
                controlId="floatingInput"
                label="Firstname"
                className="float-input my-4"
              >
                <Form.Control
                  disabled
                  type="text"
                  name="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Firstname"
                />
                {formik.touched.firstName && formik.errors.firstName && (
                  <span className="text-danger font-size-sm">
                    {formik.errors.firstName}
                  </span>
                )}
              </FloatingLabel>
              <FloatingLabel
                controlId="floatingInput"
                label="Lastname"
                className="float-input my-4"
              >
                <Form.Control
                  disabled
                  type="text"
                  name="lastName"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Lastname"
                />
                {formik.touched.lastName && formik.errors.lastName && (
                  <span className="text-danger font-size-sm">
                    {formik.errors.lastName}
                  </span>
                )}
              </FloatingLabel>
              <div className="my-4">
                <PhoneInput
                  placeholder="Enter phone number"
                  international
                  value={formik.values.phoneNumber}
                  defaultCountry="US"
                  onChange={(phone) => setPhoneNumber(phone)}
                  inputProps={{ name: "phoneNumber", required: true }}
                  countryCallingCodeEditable={false}
                  className="d-flex justify-content-between w-100"
                />
                <span className="text-danger font-size-sm">
                  {phoneValidation
                    ? phoneNumber
                      ? isValidPhoneNumber(phoneNumber)
                        ? undefined
                        : "Invalid phone number"
                      : "Phone number required"
                    : null}
                </span>
              </div>
              <FloatingLabel
                controlId="floatingInput"
                label="Email address"
                className="float-input my-4"
              >
                <Form.Control
                  disabled
                  type="email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="name@example.com"
                />
                {formik.touched.email && formik.errors.email && (
                  <span className="text-danger font-size-sm">
                    {formik.errors.email}
                  </span>
                )}
              </FloatingLabel>
              <FloatingLabel
                controlId="floatingSelect"
                label="Select Role"
                className="float-input"
              >
                <Form.Select
                  aria-label="Floating label select example"
                  name="roleName"
                  value={formik.values.roleName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="mt-4"
                >
                  <option value="">Select Role</option>
                  {rolesList?.map((role, key) => (
                    <option key={key} value={role?.name}>
                      {role?.name}
                    </option>
                  ))}
                </Form.Select>
                {formik.touched.roleName && formik.errors.roleName && (
                  <span className="text-danger font-size-sm">
                    {formik.errors.roleName}
                  </span>
                )}
              </FloatingLabel>
              <div className="bg-lighter-blue mt-2 p-2 rounded-2">
                <p className="fw-bolder mb-1">Role's Permission</p>
                {permissionByIdData?.data?.permissions?.map((role) => (
                  <p className="py-1 ps-2 m-0">&#x2022; {role}</p>
                ))}
              </div>
              <Select
                value={selectedCountry}
                onChange={(option) => setSelectedCountry(option)}
                placeholder="Country"
                options={options}
                className="my-4"
              />
              <button
                disabled={loading}
                type="submit"
                className="btn-primary w-100 rounded-5 my-3"
              >
                Update user {loading && <Spinner size="sm" />}
              </button>
            </Form>
          )}

          {subActiveTab === "password" && (
            <Form onSubmit={handleResetPass}>
              <div className="my-4 mx-1">
                <FloatingLabel
                  controlId="floatingInput"
                  label="Password"
                  className="float-input my-4"
                >
                  <Form.Control
                    defaultValue={password}
                    type={pass}
                    pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}"
                    title="Must contain at least one number and one uppercase and lowercase letter, and at least 8 or more characters"
                    minLength={8}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="font-size-normal"
                  />
                </FloatingLabel>
                <div
                  className="w-50 d-flex justify-content-end gap-3 position-relative float-end text-end px-4 pointer text-secondary"
                  style={{ marginTop: "-65px" }}
                >
                  <button
                    type="button"
                    onClick={() => generateRandomPassword()}
                    className="btn-primary font-size-sm p-1"
                  >
                    Generate
                  </button>
                  {pass === "text" ? (
                    <AiOutlineEye
                      size={25}
                      onClick={() => setPass("password")}
                      className="pointer"
                    />
                  ) : (
                    <AiOutlineEyeInvisible
                      size={25}
                      onClick={() => setPass("text")}
                      className="pointer"
                    />
                  )}
                  <IoMdCopy
                    onClick={() => handleCopyPassword()}
                    size={25}
                    className="pointer"
                  />
                </div>
                <button
                  disabled={loading}
                  type="submit"
                  className="btn-primary w-100 rounded-5 my-3"
                >
                  Update {loading && <Spinner size="sm" />}
                </button>
              </div>
            </Form>
          )}
        </div>
      )}

      {/* {activeTab === 'suspend-user' && <div>
                {!suspendConfirm && <div>
                    <p className='text-center'><img src={Suspend} width="150px" /></p>
                    <p className='fs-5 fw-bold'>Confirm suspension of selected users?</p>
                    <p className='font-size-small text-secondary'>Are you certain you wish to proceed with the suspension of the selected users?</p>
                    <div className='my-4'>
                        <button onClick={() => setSuspendConfirm(true)} className='btn-yellow py-3 rounded-5 w-100'>Suspend users</button>
                    </div>
                </div>}
                {suspendConfirm && <div>
                    <p className='text-center'><img src={ConfirmSuspend} width="150px" /></p>
                    <p className='fs-5 fw-bold'>Employee(s) suspended successfully</p>
                    <p className='font-size-small text-secondary'>Are you certain you wish to proceed with the suspension of the selected users?</p>
                    <div className='my-4'>
                        <button onClick={() => { handleHideModal(); setSuspendConfirm(false) }} className='btn-primary py-3 rounded-5 w-100'>Seen</button>
                    </div>
                </div>}
            </div>} */}
    </div>
  );
};

export default UserModalTabs;

